import React, {
    useEffect,
    useRef,
    useState
} from "react";

import {
    Save,
    RefreshCw,
    Truck,
    Package,
    User,
    CalendarDays,
    Weight,
    ArrowLeft,
    FileText,
    Mic,
    Search,
    ChevronDown,
    X
} from "lucide-react";

import {
    getInventory,
    createDelivery,
    getDeliveryById,
    updateDelivery
} from "../services/api";

import {
    useNavigate,
    useSearchParams
} from "react-router-dom";

import "./Delivery.css";


/* =========================================================
   CHAMBER DISPLAY MAPPING

   Database values remain:
   C-01, C-02, C-03, C-04

   UI displays:
   A, B, C, D
========================================================= */

const getChamberDisplayName = (chamber) => {
    const value = String(chamber || "")
        .trim()
        .toUpperCase();

    const chamberMap = {
        "C-01": "A",
        "C-02": "B",
        "C-03": "C",
        "C-04": "D",
        "01": "A",
        "02": "B",
        "03": "C",
        "04": "D",
        "1": "A",
        "2": "B",
        "3": "C",
        "4": "D",
        "A": "A",
        "B": "B",
        "C": "C",
        "D": "D"
    };

    return chamberMap[value] || value || "-";
};


/* =========================================================
   SPEECH RECOGNITION
========================================================= */

const getSpeechRecognition = () => {
    if (typeof window === "undefined") {
        return null;
    }

    const SpeechRecognition =
        window.SpeechRecognition ||
        window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
        return null;
    }

    return SpeechRecognition;
};


/* =========================================================
   NUMBER WORD CONVERTER

   Supports common spoken numbers such as:
   seven
   seven hundred
   seven thousand
   twelve thousand five hundred
========================================================= */

const numberWords = {
    zero: 0,
    one: 1,
    two: 2,
    three: 3,
    four: 4,
    five: 5,
    six: 6,
    seven: 7,
    eight: 8,
    nine: 9,
    ten: 10,
    eleven: 11,
    twelve: 12,
    thirteen: 13,
    fourteen: 14,
    fifteen: 15,
    sixteen: 16,
    seventeen: 17,
    eighteen: 18,
    nineteen: 19,
    twenty: 20,
    thirty: 30,
    forty: 40,
    fifty: 50,
    sixty: 60,
    seventy: 70,
    eighty: 80,
    ninety: 90,
    hundred: 100,
    thousand: 1000,
    lakh: 100000,
    lakhs: 100000,
    million: 1000000
};


const spokenNumberToDigits = (text) => {
    if (!text) {
        return "";
    }

    const original = String(text).trim();

    /*
       If speech already contains digits,
       use those digits.
    */

    const numericMatch =
        original.match(
            /[-+]?\d+(?:[.,]\d+)?/
        );

    if (numericMatch) {
        return numericMatch[0]
            .replace(/,/g, "");
    }

    let words = original
        .toLowerCase()
        .replace(/[^a-z\s]/g, " ")
        .split(/\s+/)
        .filter(Boolean);

    if (!words.length) {
        return "";
    }

    let total = 0;
    let current = 0;

    for (const word of words) {
        if (
            !Object.prototype.hasOwnProperty.call(
                numberWords,
                word
            )
        ) {
            continue;
        }

        const value =
            numberWords[word];

        if (value === 100) {
            current =
                current > 0
                    ? current * 100
                    : 100;
        } else if (
            value === 1000 ||
            value === 100000 ||
            value === 1000000
        ) {
            if (current === 0) {
                current = 1;
            }

            total +=
                current * value;

            current = 0;
        } else {
            current += value;
        }
    }

    const result =
        total + current;

    return result > 0
        ? String(result)
        : "";
};


/* =========================================================
   VEHICLE NUMBER FORMATTER
========================================================= */

const formatVehicleNumber = (text) => {
    if (!text) {
        return "";
    }

    let value = String(text)
        .trim()
        .toUpperCase();

    value = value.replace(
        /[^A-Z0-9\s-]/g,
        ""
    );

    /*
       Convert common spoken vehicle format:

       MH 14 AP 1111
       ↓
       MH-14-AP-1111
    */

    const parts = value
        .split(/[\s-]+/)
        .filter(Boolean);

    if (parts.length >= 3) {
        return parts.join("-");
    }

    return value;
};


/* Return today's date in local time (avoids UTC date shifts). */
const getLocalDateInputValue = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};


const VoiceInputWrapper = ({
    fieldName,
    label,
    children
}) => {
    const [isListening, setIsListening] = React.useState(false);

    const startVoiceInput = () => {
        const SpeechRecognition =
            window.SpeechRecognition ||
            window.webkitSpeechRecognition;

        if (!SpeechRecognition) {
            alert(
                "Voice input is not supported. Please use Google Chrome or Microsoft Edge."
            );
            return;
        }

        const recognition = new SpeechRecognition();

        recognition.lang =
            fieldName === "customerName"
                ? "mr-IN"
                : "en-IN";

        recognition.continuous = false;
        recognition.interimResults = false;

        recognition.onstart = () => {
            setIsListening(true);
        };

        recognition.onresult = (event) => {
            const transcript = Array.from(
                {
                    length:
                        event.results.length -
                        event.resultIndex
                },
                (_, index) =>
                    event.results[
                        event.resultIndex + index
                    ]?.[0]?.transcript || ""
            ).join(" ").trim();

            if (!transcript) return;

            const input = document.querySelector(
                `[name="${fieldName}"]`
            );

            if (!input) return;

            const setter = Object.getOwnPropertyDescriptor(
                window.HTMLInputElement.prototype,
                "value"
            )?.set;

            const textareaSetter = Object.getOwnPropertyDescriptor(
                window.HTMLTextAreaElement.prototype,
                "value"
            )?.set;

            if (input.tagName === "TEXTAREA") {
                textareaSetter?.call(input, transcript);
            } else {
                setter?.call(input, transcript);
            }

            input.dispatchEvent(
                new Event("input", { bubbles: true })
            );

            input.dispatchEvent(
                new Event("change", { bubbles: true })
            );
        };

        recognition.onerror = (event) => {
            console.error("Voice input error:", event.error);

            if (
                event.error === "not-allowed" ||
                event.error === "service-not-allowed"
            ) {
                alert("Please allow microphone access in your browser.");
            }
        };

        recognition.onend = () => {
            setIsListening(false);
        };

        try {
            recognition.start();
        } catch (error) {
            console.error("Could not start microphone:", error);
            setIsListening(false);
        }
    };

    return (
        <div
            className="delivery-voice-input"
            style={{
                position: "relative",
                width: "100%"
            }}
        >
            {children}

            <button
                type="button"
                onClick={startVoiceInput}
                title={
                    isListening
                        ? `Listening for ${label}`
                        : `Speak ${label}`
                }
                aria-label={`Speak ${label}`}
                style={{
                    position: "absolute",
                    right: "8px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    border: "none",
                    background: isListening
                        ? "#fee2e2"
                        : "#eff6ff",
                    color: isListening
                        ? "#dc2626"
                        : "#2563eb",
                    borderRadius: "8px",
                    padding: "7px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                }}
            >
                <Mic size={18} />
            </button>

            {isListening && (
                <span
                    style={{
                        display: "block",
                        fontSize: "12px",
                        color: "#dc2626",
                        marginTop: "5px"
                    }}
                >
                    Listening...
                </span>
            )}
        </div>
    );
};


/* =========================================================
   DELIVERY COMPONENT
========================================================= */

const Delivery = () => {

    const navigate =
        useNavigate();

    const [searchParams] =
        useSearchParams();


    /* =====================================================
       EDIT
    ===================================================== */

    const editId =
        searchParams.get("edit");

    const isEditMode =
        Boolean(editId);


    /* =====================================================
       FORM
    ===================================================== */

    const [form, setForm] =
        useState({

            date:
                getLocalDateInputValue(),

            inventoryId: "",

            customerName: "",

            vehicleNo: "",

            driverName: "",

            partyNo: "",

            deliveryBags: "",

            deliveryWeightKg: "",

            fullLorryWeightKg: "",

            emptyLorryWeightKg: "",

            remarks: ""
        });


    /* =====================================================
       INVENTORY
    ===================================================== */

    const [
        inventory,
        setInventory
    ] = useState([]);

    const [
        loadingInventory,
        setLoadingInventory
    ] = useState(true);


    /* =====================================================
       PAGE STATE
    ===================================================== */

    const [
        loading,
        setLoading
    ] = useState(false);

    const [
        loadingEdit,
        setLoadingEdit
    ] = useState(false);

    const [
        error,
        setError
    ] = useState("");

    const [
        success,
        setSuccess
    ] = useState("");


    /* =====================================================
       SEARCHABLE LOT DROPDOWN
    ===================================================== */

    const [
        lotDropdownOpen,
        setLotDropdownOpen
    ] = useState(false);

    const [
        lotSearch,
        setLotSearch
    ] = useState("");

    const lotDropdownRef =
        useRef(null);


    /* =====================================================
       MICROPHONE STATE
    ===================================================== */

    const [
        listeningField,
        setListeningField
    ] = useState("");

    const activeRecognitionRef = useRef(null);
    const activeVoiceFieldRef = useRef("");
    const voiceSessionActiveRef = useRef(false);
    const voiceRestartTimerRef = useRef(null);

    useEffect(() => () => {
        voiceSessionActiveRef.current = false;

        if (voiceRestartTimerRef.current) {
            window.clearTimeout(voiceRestartTimerRef.current);
        }

        activeRecognitionRef.current?.stop();
        activeRecognitionRef.current = null;
    }, []);


    /* =====================================================
       CLOSE LOT DROPDOWN WHEN CLICKING OUTSIDE
    ===================================================== */

    useEffect(() => {

        const handleOutsideClick =
            (event) => {

                if (
                    lotDropdownRef.current &&
                    !lotDropdownRef.current.contains(
                        event.target
                    )
                ) {
                    setLotDropdownOpen(
                        false
                    );
                }
            };

        document.addEventListener(
            "mousedown",
            handleOutsideClick
        );

        return () => {
            document.removeEventListener(
                "mousedown",
                handleOutsideClick
            );
        };

    }, []);


    /* =====================================================
       LOAD INVENTORY
    ===================================================== */

    const loadInventory =
        async () => {

            try {

                setLoadingInventory(
                    true
                );

                setError("");

                const response =
                    await getInventory();

                console.log(
                    "Inventory API Response:",
                    response
                );

                const responseData =
                    response?.data;

                let data = [];

                if (
                    Array.isArray(
                        responseData?.data
                    )
                ) {

                    data =
                        responseData.data;

                } else if (
                    Array.isArray(
                        responseData
                    )
                ) {

                    data =
                        responseData;

                }

                console.log(
                    "Inventory Loaded:",
                    data
                );

                setInventory(data);

            } catch (err) {

                console.error(
                    "Inventory Error:",
                    err
                );

                setInventory([]);

                setError(
                    err?.response?.data?.message ||
                    err?.message ||
                    "Unable to load available inventory."
                );

            } finally {

                setLoadingInventory(
                    false
                );
            }
        };


    /* =====================================================
       LOAD EDIT DELIVERY
    ===================================================== */

    const loadDelivery =
        async () => {

            if (!editId) {
                return;
            }

            try {

                setLoadingEdit(true);

                setError("");

                const response =
                    await getDeliveryById(
                        editId
                    );

                console.log(
                    "Delivery Edit Response:",
                    response
                );

                const delivery =
                    response?.data?.data ||
                    response?.data;

                if (!delivery) {
                    throw new Error(
                        "Delivery information not found."
                    );
                }

                const firstItem =
                    delivery.items?.[0] ||
                    {};

                setForm({

                    date:
                        delivery.challan_date ||
                        delivery.delivery_date ||
                        delivery.date ||
                        getLocalDateInputValue(),

                    inventoryId:
                        delivery.inventory_id ||
                        firstItem.inventory_id ||
                        "",

                    customerName:
                        delivery.customer_name ||
                        "",

                    vehicleNo:
                        delivery.vehicle_no ||
                        "",

                    driverName:
                        delivery.driver_name ||
                        "",

                    partyNo:
                        delivery.party_no ||
                        delivery.partyNo ||
                        "",

                    deliveryBags:
                        delivery.bags_quantity ||
                        delivery.delivery_bags ||
                        firstItem.bags_quantity ||
                        "",

                    deliveryWeightKg:
                        delivery.weight_kg ||
                        delivery.delivery_weight_kg ||
                        firstItem.weight_kg ||
                        "",

                    fullLorryWeightKg:
                        delivery.full_lorry_weight_kg ||
                        "",

                    emptyLorryWeightKg:
                        delivery.empty_lorry_weight_kg ||
                        "",

                    remarks:
                        delivery.remarks ||
                        ""
                });

            } catch (err) {

                console.error(
                    "Load Delivery Error:",
                    err
                );

                setError(
                    err?.response?.data?.message ||
                    err?.message ||
                    "Unable to load delivery."
                );

            } finally {

                setLoadingEdit(false);
            }
        };


    /* =====================================================
       INITIAL LOAD
    ===================================================== */

    useEffect(() => {

        loadInventory();

    }, []);

        useEffect(() => {

        loadDelivery();

    }, [editId]);


    /* =====================================================
       NORMAL INPUT CHANGE
    ===================================================== */

    const handleChange =
        (event) => {

            const {
                name,
                value
            } = event.target;

            setForm(
                previous => ({
                    ...previous,
                    [name]: value
                })
            );

            setError("");
            setSuccess("");
        };


    /* =====================================================
       SPEECH INPUT
    ===================================================== */

    const startVoiceInput =
        (fieldName) => {

            const SpeechRecognition =
                getSpeechRecognition();

            if (!SpeechRecognition) {

                setError(
                    "Voice input is not supported in this browser. Please use Google Chrome or Microsoft Edge."
                );

                return;
            }

            if (
                voiceSessionActiveRef.current &&
                activeVoiceFieldRef.current === fieldName
            ) {
                voiceSessionActiveRef.current = false;

                const restartPending =
                    voiceRestartTimerRef.current !== null;

                if (voiceRestartTimerRef.current) {
                    window.clearTimeout(voiceRestartTimerRef.current);
                    voiceRestartTimerRef.current = null;
                }

                if (restartPending) {
                    activeRecognitionRef.current = null;
                    activeVoiceFieldRef.current = "";
                    setListeningField("");
                } else {
                    try {
                        activeRecognitionRef.current?.stop();
                    } catch (err) {
                        console.error("Speech stop error:", err);
                    }
                }

                return;
            }

            if (activeRecognitionRef.current) {
                voiceSessionActiveRef.current = false;

                const restartPending =
                    voiceRestartTimerRef.current !== null;

                if (voiceRestartTimerRef.current) {
                    window.clearTimeout(voiceRestartTimerRef.current);
                    voiceRestartTimerRef.current = null;
                }

                if (!restartPending) {
                    try {
                        activeRecognitionRef.current.stop();
                    } catch (err) {
                        console.error("Speech stop error:", err);
                    }
                }
            }

            const recognition =
                new SpeechRecognition();

            const isCustomerName =
                fieldName === "customerName";
            const isLotSearch =
                fieldName === "lotSearch";

            recognition.lang =
                isCustomerName || isLotSearch
                    ? "mr-IN"
                    : "en-IN";

            recognition.continuous =
                isCustomerName;

            recognition.interimResults =
                isCustomerName;

            recognition.maxAlternatives =
                1;

            let baseText =
                isCustomerName
                    ? form[fieldName] || ""
                    : "";

            let finalTranscript = "";
            let recognizedText = baseText;

            activeRecognitionRef.current = recognition;
            activeVoiceFieldRef.current = fieldName;
            voiceSessionActiveRef.current = true;

            setListeningField(fieldName);
            setError("");

            recognition.onstart =
                () => {
                    setListeningField(fieldName);
                };

            recognition.onresult =
                (event) => {

                    if (isCustomerName) {

                        let interimTranscript = "";

                        for (
                            let index = event.resultIndex;
                            index < event.results.length;
                            index += 1
                        ) {
                            const transcript =
                                event.results[index][0].transcript;

                            if (event.results[index].isFinal) {
                                finalTranscript += `${transcript} `;
                            } else {
                                interimTranscript += transcript;
                            }
                        }

                        const spokenText = (
                            finalTranscript + interimTranscript
                        ).trim();

                        const committedText =
                            finalTranscript.trim();

                        recognizedText = committedText
                            ? `${baseText} ${committedText}`.trim()
                            : baseText;

                        const displayText = spokenText
                            ? `${baseText} ${spokenText}`.trim()
                            : baseText;

                        setForm((previous) => ({
                            ...previous,
                            [fieldName]: displayText
                        }));

                        setError("");
                        setSuccess("");

                        return;
                    }

                    const transcript = Array.from(
                        {
                            length:
                                event.results.length -
                                event.resultIndex
                        },
                        (_, offset) =>
                            event.results[
                                event.resultIndex + offset
                            ]?.[0]?.transcript || ""
                    ).join(" ").trim();

                    if (!transcript) {
                        return;
                    }

                    let finalValue =
                        transcript;

                    /*
                       Number fields
                    */

                    const numberFields = [
                        "deliveryBags",
                        "deliveryWeightKg",
                        "fullLorryWeightKg",
                        "emptyLorryWeightKg"
                    ];

                    if (
                        numberFields.includes(
                            fieldName
                        )
                    ) {
                        finalValue =
                            spokenNumberToDigits(
                                transcript
                            );
                    }

                    /*
                       Vehicle number
                    */

                    if (
                        fieldName === "vehicleNo"
                    ) {
                        finalValue =
                            formatVehicleNumber(
                                transcript
                            );
                    }

                    /*
                       Text fields
                    */

                    if (isLotSearch) {
                        setLotSearch(finalValue);
                    } else {
                        setForm(
                            previous => ({
                                ...previous,
                                [fieldName]: finalValue
                            })
                        );
                    }

                    setError("");
                    setSuccess("");
                };

            recognition.onerror =
                (event) => {

                    console.error(
                        "Speech Recognition Error:",
                        event.error
                    );

                    if (
                        event.error === "not-allowed" ||
                        event.error === "service-not-allowed"
                    ) {
                        setError(
                            "Microphone permission denied. Allow microphone access in your browser settings."
                        );

                        voiceSessionActiveRef.current = false;
                        setListeningField("");

                        return;
                    }

                    if (
                        event.error === "no-speech"
                    ) {
                        return;
                    }

                    if (
                        event.error === "aborted"
                    ) {
                        return;
                    }

                    setError(
                        "Voice input failed. Please try again."
                    );
                };

            recognition.onend =
                () => {

                    if (
                        isCustomerName &&
                        voiceSessionActiveRef.current &&
                        activeVoiceFieldRef.current === fieldName
                    ) {

                        if (voiceRestartTimerRef.current) {
                            window.clearTimeout(
                                voiceRestartTimerRef.current
                            );
                        }

                        voiceRestartTimerRef.current =
                            window.setTimeout(() => {

                                voiceRestartTimerRef.current = null;

                                if (
                                    !voiceSessionActiveRef.current ||
                                    activeVoiceFieldRef.current !== fieldName
                                ) {
                                    return;
                                }

                                try {
                                    const currentRecognition =
                                        new SpeechRecognition();

                                    currentRecognition.lang = "mr-IN";
                                    currentRecognition.continuous = true;
                                    currentRecognition.interimResults = true;
                                    currentRecognition.maxAlternatives = 1;

                                    activeRecognitionRef.current =
                                        currentRecognition;

                                    /*
                                       Keep the customer-name microphone
                                       active for continued Marathi input.
                                    */

                                    currentRecognition.onresult =
                                        (nextEvent) => {

                                            let interimText = "";

                                            for (
                                                let index = nextEvent.resultIndex;
                                                index < nextEvent.results.length;
                                                index += 1
                                            ) {
                                                const text =
                                                    nextEvent.results[index][0].transcript;

                                                if (
                                                    nextEvent.results[index].isFinal
                                                ) {
                                                    finalTranscript += `${text} `;
                                                } else {
                                                    interimText += text;
                                                }
                                            }

                                            const spokenText = (
                                                finalTranscript + interimText
                                            ).trim();

                                            recognizedText =
                                                finalTranscript.trim()
                                                    ? `${baseText} ${finalTranscript.trim()}`.trim()
                                                    : baseText;

                                            setForm(previous => ({
                                                ...previous,
                                                [fieldName]: spokenText
                                                    ? `${baseText} ${spokenText}`.trim()
                                                    : baseText
                                            }));
                                        };

                                    currentRecognition.onerror =
                                        (nextError) => {
                                            console.error(
                                                "Speech Recognition Error:",
                                                nextError.error
                                            );
                                        };

                                    currentRecognition.onend =
                                        () => {
                                            if (
                                                voiceSessionActiveRef.current &&
                                                activeVoiceFieldRef.current === fieldName
                                            ) {
                                                setListeningField(fieldName);
                                            }
                                        };

                                    currentRecognition.start();

                                } catch (err) {
                                    console.error(
                                        "Speech Restart Error:",
                                        err
                                    );

                                    voiceSessionActiveRef.current = false;
                                    setListeningField("");
                                }

                            }, 250);

                        return;
                    }

                    if (
                        activeVoiceFieldRef.current === fieldName
                    ) {
                        voiceSessionActiveRef.current = false;
                        activeVoiceFieldRef.current = "";
                        activeRecognitionRef.current = null;
                        setListeningField("");
                    }
                };

            try {

                recognition.start();

            } catch (err) {

                console.error(
                    "Speech Start Error:",
                    err
                );

                voiceSessionActiveRef.current = false;
                activeVoiceFieldRef.current = "";
                activeRecognitionRef.current = null;
                setListeningField("");

                setError(
                    "Unable to start voice input. Please try again."
                );
            }
        };


    /* =====================================================
       STOP VOICE INPUT
    ===================================================== */

    const stopVoiceInput =
        () => {

            voiceSessionActiveRef.current = false;

            if (voiceRestartTimerRef.current) {
                window.clearTimeout(
                    voiceRestartTimerRef.current
                );

                voiceRestartTimerRef.current = null;
            }

            try {
                activeRecognitionRef.current?.stop();
            } catch (err) {
                console.error(
                    "Speech Stop Error:",
                    err
                );
            }

            activeRecognitionRef.current = null;
            activeVoiceFieldRef.current = "";
            setListeningField("");
        };


    /* =====================================================
       VOICE BUTTON
    ===================================================== */

    const renderVoiceButton =
        (fieldName) => (

            <button
                type="button"
                className={`voice-button ${
                    listeningField === fieldName
                        ? "listening"
                        : ""
                }`}
                onClick={() => {
                    if (
                        listeningField === fieldName
                    ) {
                        stopVoiceInput();
                    } else {
                        startVoiceInput(fieldName);
                    }
                }}
                title={
                    listeningField === fieldName
                        ? "Stop microphone"
                        : "Speak"
                }
            >

                <Mic
                    size={17}
                />

                {listeningField === fieldName
                    ? "Listening..."
                    : "Speak"}

            </button>
        );


    /* =====================================================
       SELECTED INVENTORY ITEM
    ===================================================== */

    const selectedInventory =
        inventory.find(
            item =>
                String(
                    item.id ??
                    item.inventory_id ??
                    ""
                ) === String(form.inventoryId)
        );


    /* =====================================================
       SELECT INVENTORY
    ===================================================== */

    const handleInventorySelect =
        (item) => {

            const inventoryId =
                item.id ??
                item.inventory_id;

            setForm(previous => ({
                ...previous,
                inventoryId: String(inventoryId),
                customerName:
                    previous.customerName ||
                    item.customer_name ||
                    item.customerName ||
                    ""
            }));

            setLotSearch("");
            setLotDropdownOpen(false);
            setError("");
            setSuccess("");
        };

    /* =====================================================
       FILTERED LOTS
    ===================================================== */

    const filteredInventory =
        inventory.filter(
            item => {

                const lotNo =
                    item.lot_no ||
                    item.lotNo ||
                    "";

                const chamberNo =
                    item.chamber_no ||
                    item.chamberNo ||
                    item.chamber_number ||
                    "";

                const location =
                    item.location ||
                    "";

                const customer =
                    item.customer_name ||
                    item.customerName ||
                    "";

                const productMark =
                    item.product_mark ||
                    item.productMark ||
                    "";

                const chamberName =
                    getChamberDisplayName(
                        chamberNo
                    );

                const search =
                    lotSearch
                        .trim()
                        .toLowerCase();

                if (!search) {
                    return true;
                }

                return (
                    String(lotNo)
                        .toLowerCase()
                        .includes(search) ||

                    String(chamberNo)
                        .toLowerCase()
                        .includes(search) ||

                    String(chamberName)
                        .toLowerCase()
                        .includes(search) ||

                    String(location)
                        .toLowerCase()
                        .includes(search) ||

                    String(customer)
                        .toLowerCase()
                        .includes(search) ||

                    String(productMark)
                        .toLowerCase()
                        .includes(search)
                );
            }
        );


    /* =====================================================
       GET SELECTED LOT DISPLAY
    ===================================================== */

    const getSelectedLotText =
        () => {

            if (!selectedInventory) {
                return "Select Lot";
            }

            const lotNo =
                selectedInventory.lot_no ||
                selectedInventory.lotNo ||
                "-";

            const chamberNo =
                selectedInventory.chamber_no ||
                selectedInventory.chamberNo ||
                selectedInventory.chamber_number ||
                "-";

            const location =
                selectedInventory.location ||
                "-";

            const customer =
                selectedInventory.customer_name ||
                selectedInventory.customerName ||
                "";

            const bags =
                Number(
                    selectedInventory.remaining_bags ||
                    0
                );

            return `Lot ${lotNo} — Chamber ${getChamberDisplayName(
                chamberNo
            )} — ${location} — ${customer} — ${bags.toLocaleString(
                "en-IN"
            )} bags`;
        };


    /* =====================================================
       CALCULATE NET WEIGHT
    ===================================================== */

    const fullWeight =
        Number(
            form.fullLorryWeightKg
        ) || 0;

    const emptyWeight =
        Number(
            form.emptyLorryWeightKg
        ) || 0;

    const calculatedNetWeight =
        fullWeight > 0 &&
        emptyWeight > 0 &&
        fullWeight >= emptyWeight
            ? fullWeight -
              emptyWeight
            : 0;


    /* =====================================================
       SUBMIT
    ===================================================== */

    const handleSubmit =
        async (event) => {

            event.preventDefault();

            setError("");
            setSuccess("");


            /* =================================================
               VALIDATION
            ================================================= */

            if (!form.date) {

                setError(
                    "Please select delivery date."
                );

                return;
            }


            if (!form.inventoryId) {

                setError(
                    "Please select a lot."
                );

                return;
            }


            if (
                !form.customerName.trim()
            ) {

                setError(
                    "Please enter customer name."
                );

                return;
            }


            if (
                !form.deliveryBags ||
                Number(
                    form.deliveryBags
                ) <= 0
            ) {

                setError(
                    "Please enter valid delivery bags."
                );

                return;
            }


            if (
                !form.deliveryWeightKg ||
                Number(
                    form.deliveryWeightKg
                ) <= 0
            ) {

                setError(
                    "Please enter valid delivery weight."
                );

                return;
            }


            /* =================================================
               AVAILABLE STOCK VALIDATION
            ================================================= */

            if (
                selectedInventory &&
                !isEditMode
            ) {

                const remainingBags =
                    Number(
                        selectedInventory.remaining_bags ||
                        0
                    );

                const remainingWeight =
                    Number(
                        selectedInventory.remaining_weight_kg ||
                        0
                    );


                if (
                    Number(
                        form.deliveryBags
                    ) > remainingBags
                ) {

                    setError(
                        `Only ${remainingBags.toLocaleString(
                            "en-IN"
                        )} bags are available in this lot.`
                    );

                    return;
                }


                if (
                    Number(
                        form.deliveryWeightKg
                    ) > remainingWeight
                ) {

                    setError(
                        `Only ${remainingWeight.toLocaleString(
                            "en-IN"
                        )} KG is available in this lot.`
                    );

                    return;
                }
            }


            /* =================================================
               PAYLOAD
            ================================================= */

            const payload = {

                challanDate:
                    form.date,

                deliveryDate:
                    form.date,

                inventoryId:
                    Number(
                        form.inventoryId
                    ),

                customerName:
                    form.customerName.trim(),

                vehicleNo:
                    form.vehicleNo.trim(),

                driverName:
                    form.driverName.trim(),

                partyNo:
                    form.partyNo.trim(),

                deliveryBags:
                    Number(
                        form.deliveryBags
                    ),

                deliveryWeightKg:
                    Number(
                        form.deliveryWeightKg
                    ),

                fullLorryWeightKg:
                    fullWeight,

                emptyLorryWeightKg:
                    emptyWeight,

                netWeightKg:
                    calculatedNetWeight,

                remarks:
                    form.remarks.trim()
            };


            console.log(
                "Delivery Payload:",
                payload
            );


            /* =================================================
               SAVE
            ================================================= */

            try {

                setLoading(true);

                let response;


                if (isEditMode) {

                    response =
                        await updateDelivery(
                            editId,
                            payload
                        );

                } else {

                    response =
                        await createDelivery(
                            payload
                        );
                }


                console.log(
                    "Delivery Save Response:",
                    response
                );


                const data =
                    response?.data?.data ||
                    response?.data;


                setSuccess(
                    isEditMode
                        ? "Delivery challan updated successfully."
                        : "Delivery challan created successfully."
                );


                /* =================================================
                   GET CHALLAN ID
                ================================================= */

                const challanId =
                    data?.id ||
                    data?.challan_id ||
                    data?.delivery_id ||
                    data?.challan?.id ||
                    data?.delivery?.id ||
                    editId;


                if (challanId) {

                    navigate(
                        `/delivery-challans/print/${challanId}`
                    );

                } else {

                    setError(
                        "Delivery challan was saved, but the challan ID was not returned by the server."
                    );
                }

            } catch (err) {

                console.error(
                    "Delivery Save Error:",
                    err
                );

                setError(
                    err?.response?.data?.message ||
                    err?.message ||
                    "Unable to save delivery challan."
                );

            } finally {

                setLoading(false);
            }
        };

            /* =====================================================
       RESET
    ===================================================== */

    const handleReset =
        () => {

            setForm({

                date:
                    getLocalDateInputValue(),

                inventoryId: "",

                customerName: "",

                vehicleNo: "",

                driverName: "",

                partyNo: "",

                deliveryBags: "",

                deliveryWeightKg: "",

                fullLorryWeightKg: "",

                emptyLorryWeightKg: "",

                remarks: ""
            });

            setLotSearch("");

            setLotDropdownOpen(
                false
            );

            setError("");
            setSuccess("");
        };


    /* =====================================================
       LOADING EDIT
    ===================================================== */

    if (loadingEdit) {

        return (
            <div className="delivery-page">

                <div className="delivery-loading">

                    <RefreshCw
                        size={30}
                        className="delivery-spin"
                    />

                    <h2>
                        Loading Delivery Challan
                    </h2>

                    <p>
                        Loading existing challan information...
                    </p>

                </div>

            </div>
        );
    }


    /* =====================================================
       PAGE
    ===================================================== */

    return (

        <div className="delivery-page">


            {/* =================================================
                HEADER
            ================================================= */}

            <div className="delivery-page-header">

                <div className="delivery-heading">

                    <div className="delivery-heading-icon">

                        <Truck
                            size={27}
                        />

                    </div>

                    <div>

                        <h1>

                            {
                                isEditMode
                                    ? "Edit Delivery Challan"
                                    : "Delivery Challan"
                            }

                        </h1>

                        <p>

                            {
                                isEditMode
                                    ? "Update existing delivery challan"
                                    : "Create delivery challan"
                            }

                        </p>

                    </div>

                </div>


                <div className="delivery-header-actions">

                    <button
                        type="button"
                        className="delivery-back-btn"
                        onClick={() =>
                            navigate(
                                "/delivery-challans"
                            )
                        }
                    >

                        <ArrowLeft
                            size={18}
                        />

                        Back

                    </button>


                    <button
                        type="button"
                        className="delivery-refresh-btn"
                        onClick={loadInventory}
                        disabled={loadingInventory}
                    >

                        <RefreshCw
                            size={18}
                            className={
                                loadingInventory
                                    ? "delivery-spin"
                                    : ""
                            }
                        />

                        Refresh Inventory

                    </button>

                </div>

            </div>


            {/* =================================================
                ERROR
            ================================================= */}

            {error && (

                <div className="delivery-alert delivery-alert-error">

                    <span>
                        {error}
                    </span>

                    <button
                        type="button"
                        onClick={() => setError("")}
                        aria-label="Close error"
                    >

                        <X
                            size={18}
                        />

                    </button>

                </div>

            )}


            {/* =================================================
                SUCCESS
            ================================================= */}

            {success && (

                <div className="delivery-alert delivery-alert-success">

                    <span>
                        {success}
                    </span>

                    <button
                        type="button"
                        onClick={() => setSuccess("")}
                        aria-label="Close message"
                    >

                        <X
                            size={18}
                        />

                    </button>

                </div>

            )}


            {/* =================================================
                FORM
            ================================================= */}

            <form
                className="delivery-form"
                onSubmit={handleSubmit}
            >


                {/* =================================================
                    DELIVERY INFORMATION
                ================================================= */}

                <section className="delivery-section">

                    <div className="delivery-section-heading">

                        <div className="delivery-section-icon">

                            <FileText
                                size={20}
                            />

                        </div>

                        <div>

                            <h2>
                                Delivery Information
                            </h2>

                            <p>
                                Enter the basic delivery challan details.
                            </p>

                        </div>

                    </div>


                    <div className="delivery-form-grid">


                        {/* DELIVERY DATE */}

                        <div className="delivery-field">

                            <label>
                                Delivery Date
                                <span className="required">*</span>
                            </label>

                            <div className="delivery-input-icon">

                                <CalendarDays
                                    size={18}
                                />

                                <input
                                    type="date"
                                    name="date"
                                    value={
                                        form.date
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    required
                                />

                            </div>

                        </div>


                        {/* LOT SELECTION */}

                        <div
                            className="delivery-field delivery-lot-field"
                            ref={lotDropdownRef}
                        >

                            <label>
                                Select Lot
                                <span className="required">*</span>
                            </label>

                            <button
                                type="button"
                                className={`delivery-lot-select ${
                                    lotDropdownOpen
                                        ? "active"
                                        : ""
                                }`}
                                onClick={() => {
                                    setLotDropdownOpen(
                                        previous => !previous
                                    );
                                }}
                                disabled={loadingInventory}
                            >

                                <span>
                                    {
                                        loadingInventory
                                            ? "Loading inventory..."
                                            : getSelectedLotText()
                                    }
                                </span>

                                <ChevronDown
                                    size={18}
                                />

                            </button>


                            {lotDropdownOpen && (

                                <div className="delivery-lot-dropdown">

                                    <div className="delivery-lot-search">
                                        <Search size={19} />

                                        <input
                                            type="text"
                                            value={lotSearch}
                                            onChange={(event) =>
                                                setLotSearch(event.target.value)
                                            }
                                            placeholder="Search lot, chamber or customer..."
                                        />

                                        <button
                                            type="button"
                                            className={`delivery-search-mic ${
                                                listeningField === "lotSearch"
                                                    ? "listening"
                                                    : ""
                                            }`}
                                            title="मराठीत बोलून शोधा"
                                            aria-label="मराठीत बोलून शोधा"
                                            onClick={() => startVoiceInput("lotSearch")}
                                        >
                                            <Mic size={19} />
                                        </button>
                                    </div>


                                    <div className="delivery-lot-options">

                                        {filteredInventory.length > 0 ? (

                                            filteredInventory.map(
                                                (item) => {

                                                    const itemId =
                                                        item.id;

                                                    const lotNo =
                                                        item.lot_no ||
                                                        item.lotNo ||
                                                        "-";

                                                    const chamberNo =
                                                        item.chamber_no ||
                                                        item.chamberNo ||
                                                        item.chamber_number ||
                                                        "-";

                                                    const location =
                                                        item.location ||
                                                        "-";

                                                    const customer =
                                                        item.customer_name ||
                                                        item.customerName ||
                                                        "-";

                                                    const bags =
                                                        Number(
                                                            item.remaining_bags ||
                                                            0
                                                        );

                                                    return (

                                                        <button
                                                            key={itemId}
                                                            type="button"
                                                            className={`delivery-lot-option ${
                                                                String(
                                                                    form.inventoryId
                                                                ) === String(itemId)
                                                                    ? "selected"
                                                                    : ""
                                                            }`}
                                                            onClick={() =>
                                                                handleInventorySelect(
                                                                    item
                                                                )
                                                            }
                                                        >

                                                            <div className="delivery-lot-option-top">

                                                                <strong>
                                                                    Lot {lotNo}
                                                                </strong>

                                                                <span>
                                                                    Chamber {
                                                                        getChamberDisplayName(
                                                                            chamberNo
                                                                        )
                                                                    }
                                                                </span>

                                                            </div>

                                                            <div className="delivery-lot-option-details">

                                                                <span>
                                                                    {location}
                                                                </span>

                                                                <span>
                                                                    {customer}
                                                                </span>

                                                            </div>

                                                            <div className="delivery-lot-option-stock">

                                                                Available: {
                                                                    bags.toLocaleString(
                                                                        "en-IN"
                                                                    )
                                                                } bags

                                                            </div>

                                                        </button>

                                                    );
                                                }
                                            )

                                        ) : (

                                            <div className="delivery-lot-empty">

                                                <Package
                                                    size={25}
                                                />

                                                <p>
                                                    No matching lots found.
                                                </p>

                                            </div>

                                        )}

                                    </div>

                                </div>

                            )}

                        </div>


                        {/* CUSTOMER NAME */}

                        <div className="delivery-field">

                            <label>
                                Customer Name
                                <span className="required">*</span>
                            </label>

                            <VoiceInputWrapper
                                fieldName="customerName"
                                label="customer name"
                            >

                                <input
                                    type="text"
                                    name="customerName"
                                    value={
                                        form.customerName
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    placeholder="Enter customer name in Marathi or English"
                                    required
                                    style={{
                                        paddingRight:
                                            "60px"
                                    }}
                                />

                            </VoiceInputWrapper>

                        </div>


                        {/* VEHICLE NUMBER */}

                        <div className="delivery-field">

                            <label>
                                Vehicle Number
                            </label>

                            <VoiceInputWrapper
                                fieldName="vehicleNo"
                                label="vehicle number"
                            >

                                <input
                                    type="text"
                                    name="vehicleNo"
                                    value={
                                        form.vehicleNo
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    placeholder="e.g. MH-14-AB-1234"
                                    style={{
                                        paddingRight:
                                            "60px"
                                    }}
                                />

                            </VoiceInputWrapper>

                        </div>


                        {/* DRIVER NAME */}

                        <div className="delivery-field">

                            <label>
                                Driver Name
                            </label>

                            <VoiceInputWrapper
                                fieldName="driverName"
                                label="driver name"
                            >

                                <input
                                    type="text"
                                    name="driverName"
                                    value={
                                        form.driverName
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    placeholder="Enter driver name"
                                    style={{
                                        paddingRight:
                                            "60px"
                                    }}
                                />

                            </VoiceInputWrapper>

                        </div>


                        {/* PARTY NUMBER */}

                        <div className="delivery-field">

                            <label>
                                Party Number
                            </label>

                            <VoiceInputWrapper
                                fieldName="partyNo"
                                label="party number"
                            >

                                <input
                                    type="text"
                                    name="partyNo"
                                    value={
                                        form.partyNo
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    placeholder="Enter party number"
                                    style={{
                                        paddingRight:
                                            "60px"
                                    }}
                                />

                            </VoiceInputWrapper>

                        </div>

                        {/* DELIVERY BAGS */}

                        <div className="delivery-field">

                            <label>
                                Delivery Bags
                                <span className="required">*</span>
                            </label>

                            <VoiceInputWrapper
                                fieldName="deliveryBags"
                                label="delivery bags"
                            >
                                <input
                                    type="number"
                                    name="deliveryBags"
                                    value={form.deliveryBags}
                                    onChange={handleChange}
                                    min="1"
                                    step="1"
                                    placeholder="Enter number of bags"
                                    required
                                    style={{ paddingRight: "60px" }}
                                />
                            </VoiceInputWrapper>

                        </div>

                        {/* DELIVERY WEIGHT */}

                        <div className="delivery-field">

                            <label>
                                Delivery Weight KG
                                <span className="required">*</span>
                            </label>

                            <VoiceInputWrapper
                                fieldName="deliveryWeightKg"
                                label="delivery weight in kilograms"
                            >
                                <input
                                    type="number"
                                    name="deliveryWeightKg"
                                    value={form.deliveryWeightKg}
                                    onChange={handleChange}
                                    min="0.01"
                                    step="any"
                                    placeholder="Enter delivery weight in KG"
                                    required
                                    style={{ paddingRight: "60px" }}
                                />
                            </VoiceInputWrapper>

                        </div>

                    </div>

                </section>

                                {/* =================================================
                    LORRY WEIGHT
                ================================================= */}

                <section className="delivery-section">

                    <div className="delivery-section-header">

                        <div className="delivery-section-icon orange">

                            <Weight size={20} />

                        </div>

                        <div>

                            <h2>
                                Lorry Weight
                            </h2>

                            <p>
                                Enter vehicle weight details
                            </p>

                        </div>

                    </div>

                    <div className="delivery-form-grid">

                        {/* FULL WEIGHT */}

                        <div className="delivery-field">

                            <label>
                                Full Lorry Weight KG
                            </label>

                            <VoiceInputWrapper
                                fieldName="fullLorryWeightKg"
                                label="full lorry weight"
                            >

                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    name="fullLorryWeightKg"
                                    value={form.fullLorryWeightKg}
                                    onChange={handleChange}
                                    placeholder="Full lorry weight"
                                    style={{
                                        paddingRight: "60px"
                                    }}
                                />

                            </VoiceInputWrapper>

                        </div>

                        {/* EMPTY WEIGHT */}

                        <div className="delivery-field">

                            <label>
                                Empty Lorry Weight KG
                            </label>

                            <VoiceInputWrapper
                                fieldName="emptyLorryWeightKg"
                                label="empty lorry weight"
                            >

                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    name="emptyLorryWeightKg"
                                    value={form.emptyLorryWeightKg}
                                    onChange={handleChange}
                                    placeholder="Empty lorry weight"
                                    style={{
                                        paddingRight: "60px"
                                    }}
                                />

                            </VoiceInputWrapper>

                        </div>

                        {/* NET WEIGHT */}

                        <div className="delivery-net-weight">

                            <span>
                                Net Weight
                            </span>

                            <strong>
                                {calculatedNetWeight.toLocaleString("en-IN")}
                                {" "}
                                KG
                            </strong>

                        </div>

                    </div>

                </section>

                {/* =================================================
                    REMARKS
                ================================================= */}

                <section className="delivery-section">

                    <div className="delivery-field">

                        <label>
                            Remarks
                        </label>

                        <VoiceInputWrapper
                            fieldName="remarks"
                            label="remarks"
                        >

                            <textarea
                                name="remarks"
                                value={form.remarks}
                                onChange={handleChange}
                                rows="4"
                                placeholder="Enter remarks..."
                                style={{
                                    paddingRight: "60px"
                                }}
                            />

                        </VoiceInputWrapper>

                    </div>

                </section>

                {/* =================================================
                    ACTIONS
                ================================================= */}

                <div className="delivery-form-actions">

                    {/* CANCEL */}

                    <button
                        type="button"
                        className="delivery-cancel-button"
                        onClick={() =>
                            navigate("/delivery-challans")
                        }
                        disabled={loading}
                    >

                        <ArrowLeft size={18} />

                        Cancel

                    </button>

                    {/* RESET */}

                    <button
                        type="button"
                        className="delivery-reset-button"
                        onClick={handleReset}
                        disabled={loading}
                    >

                        Reset

                    </button>

                    {/* SAVE */}

                    <button
                        type="submit"
                        className="delivery-save-button"
                        disabled={loading || loadingInventory}
                    >

                        {loading ? (

                            <>

                                <RefreshCw
                                    size={18}
                                    className="delivery-spin"
                                />

                                {isEditMode
                                    ? "Updating..."
                                    : "Saving..."}

                            </>

                        ) : (

                            <>

                                <Save size={18} />

                                {isEditMode
                                    ? "Update Challan"
                                    : "Save & Generate Challan"}

                            </>

                        )}

                    </button>

                </div>

            </form>

        </div>
    );
};

export default Delivery;